/**
 * AssetCard — individual asset in grid or list mode.
 *
 * Supports checkbox selection, drag, right-click context menu,
 * and displays status badges, tags, and color labels.
 * Grid mode shows thumbnail previews for image assets.
 *
 * Thumbnail URLs are stabilized at the data layer (ProjectDashboardPage
 * preserves URLs across re-fetches), so this component just renders
 * the URL it receives — no caching needed here.
 */

import { useState, useEffect } from 'react'
import { Film, Image, Music, FileText, Loader2 } from 'lucide-react'
import type { Asset, ViewMode } from '../../types/fileManager'
import { isVideoAsset } from '../../utils/mediaType'
import StatusBadge from './StatusBadge'

interface AssetCardProps {
  asset: Asset
  viewMode: ViewMode
  isSelected: boolean
  onToggleSelect: (id: number, additive: boolean) => void
  onContextMenu: (e: React.MouseEvent, asset: Asset) => void
  onDragStart: (e: React.DragEvent, asset: Asset) => void
  onStatusChange?: (assetId: number, status: string) => void
}

const COLOR_BORDER: Record<string, string> = {
  red: 'border-l-red-500',
  orange: 'border-l-orange-500',
  yellow: 'border-l-yellow-500',
  green: 'border-l-emerald-500',
  blue: 'border-l-blue-500',
  purple: 'border-l-purple-500',
}

function getFileIcon(fileType: string) {
  if (fileType?.startsWith('video/')) return <Film className="w-5 h-5 text-[var(--rq-blue)]" />
  if (fileType?.startsWith('audio/')) return <Music className="w-5 h-5 text-[var(--rq-amber)]" />
  if (fileType?.startsWith('image/')) return <Image className="w-5 h-5 text-emerald-400" />
  return <FileText className="w-5 h-5 text-amber-400" />
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function AssetCard({
  asset,
  viewMode,
  isSelected,
  onToggleSelect,
  onContextMenu,
  onDragStart,
  onStatusChange,
}: AssetCardProps) {
  const [imgError, setImgError] = useState(false)

  // Reset error state when the asset or its thumbnail URL changes
  useEffect(() => {
    setImgError(false)
  }, [asset.id, asset.thumbnail_url])

  const handleClick = (e: React.MouseEvent) => {
    onToggleSelect(asset.id, e.metaKey || e.ctrlKey || e.shiftKey)
  }

  const colorBorder = asset.color_label ? COLOR_BORDER[asset.color_label] : ''
  const tags = asset.tags || []
  const hasThumbnail = !!asset.thumbnail_url && !imgError
  const isVideo = isVideoAsset(asset)
  const ingestStatus = isVideo ? asset.status : null

  if (viewMode === 'list') {
    return (
      <div
        draggable
        onDragStart={e => onDragStart(e, asset)}
        onClick={handleClick}
        onContextMenu={e => { e.preventDefault(); onContextMenu(e, asset) }}
        className={`fm-asset-card flex items-center gap-3 px-3 py-2.5 ${colorBorder ? `border-l-2 ${colorBorder}` : ''} ${isSelected ? 'fm-asset-card-selected' : ''}`}
      >
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          className="w-4 h-4 rounded border-white/20 flex-shrink-0 accent-[var(--rq-blue)]"
          onClick={e => e.stopPropagation()}
        />
        {getFileIcon(asset.file_type)}
        {ingestStatus === 'ingesting' && (
          <Loader2 className="w-3 h-3 text-amber-400 animate-spin flex-shrink-0" />
        )}
        {ingestStatus === 'uploaded' && (
          <span className="w-2 h-2 rounded-full bg-amber-400/60 flex-shrink-0" title="Not analyzed" />
        )}
        {ingestStatus === 'failed' && (
          <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" title="Analysis failed" />
        )}
        <span className="flex-1 min-w-0 text-sm text-[var(--rq-text)] truncate">{asset.original_filename}</span>
        <div className="flex-shrink-0">
          <StatusBadge
            status={asset.editorial_status}
            priority={asset.priority}
            onClick={onStatusChange ? (next) => onStatusChange(asset.id, next) : undefined}
          />
        </div>
        {tags.length > 0 && (
          <div className="flex-shrink-0 hidden lg:flex items-center gap-1 max-w-[120px]">
            {tags.slice(0, 2).map(t => (
              <span key={t} className="fm-tag-pill px-1.5 py-0.5 text-[10px] truncate max-w-[56px]">{t}</span>
            ))}
            {tags.length > 2 && (
              <span className="text-[10px] text-[var(--rq-text-soft)]">+{tags.length - 2}</span>
            )}
          </div>
        )}
        <span className="text-xs text-[var(--rq-text-soft)] w-20 text-right flex-shrink-0">{formatFileSize(asset.file_size)}</span>
        <span className="text-xs text-[var(--rq-text-soft)] w-28 text-right flex-shrink-0 hidden md:block">{formatDate(asset.upload_time)}</span>
      </div>
    )
  }

  // Grid mode
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, asset)}
      onClick={handleClick}
      onContextMenu={e => { e.preventDefault(); onContextMenu(e, asset) }}
      className={`fm-asset-card rounded-xl flex flex-col cursor-pointer group relative overflow-hidden ${
        hasThumbnail ? 'p-0 pb-2' : 'p-3 items-center gap-2'
      } ${colorBorder ? `border-l-2 ${colorBorder}` : ''} ${isSelected ? 'fm-asset-card-selected' : ''}`}
    >
      {/* Checkbox - visible on hover or selected */}
      <div className={`absolute top-2 left-2 z-10 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
        <input
          type="checkbox"
          checked={isSelected}
          readOnly
          className="w-4 h-4 rounded border-white/20 accent-[var(--rq-blue)]"
          onClick={e => e.stopPropagation()}
        />
      </div>

      {/* Status badge - top right */}
      {asset.editorial_status && asset.editorial_status !== 'needs_review' && (
        <div className="absolute top-2 right-2 z-10">
          <StatusBadge
            status={asset.editorial_status}
            onClick={onStatusChange ? (next) => onStatusChange(asset.id, next) : undefined}
          />
        </div>
      )}

      {/* Thumbnail or icon */}
      {hasThumbnail ? (
        <div className="relative w-full aspect-video bg-black/30 rounded-t-xl overflow-hidden">
          <img
            src={asset.thumbnail_url!}
            alt={asset.original_filename}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setImgError(true)}
          />
          {ingestStatus === 'ingesting' && (
            <div className="absolute bottom-1.5 right-1.5 flex items-center gap-1 bg-black/70 rounded px-1.5 py-0.5">
              <Loader2 className="w-3 h-3 text-amber-400 animate-spin" />
              <span className="text-[9px] text-amber-300">Analyzing</span>
            </div>
          )}
          {ingestStatus === 'uploaded' && (
            <div className="absolute bottom-1.5 right-1.5 bg-black/70 rounded px-1.5 py-0.5">
              <span className="text-[9px] text-amber-300/80">Unanalyzed</span>
            </div>
          )}
          {ingestStatus === 'failed' && (
            <div className="absolute bottom-1.5 right-1.5 bg-black/70 rounded px-1.5 py-0.5">
              <span className="text-[9px] text-red-400">Failed</span>
            </div>
          )}
        </div>
      ) : (
        <div className="relative w-12 h-12 flex items-center justify-center bg-white/5 rounded-lg">
          {getFileIcon(asset.file_type)}
          {ingestStatus === 'ingesting' && (
            <Loader2 className="absolute -bottom-1 -right-1 w-3 h-3 text-amber-400 animate-spin" />
          )}
        </div>
      )}

      {/* Filename */}
      <p className={`text-xs text-[var(--rq-text)] truncate w-full ${hasThumbnail ? 'px-2 mt-1.5' : 'text-center'}`}>
        {asset.original_filename}
      </p>

      {/* Meta */}
      <p className={`text-[10px] text-[var(--rq-text-soft)] ${hasThumbnail ? 'px-2' : ''}`}>
        {formatFileSize(asset.file_size)}
      </p>

      {/* Tags - bottom */}
      {tags.length > 0 && (
        <div className={`flex items-center gap-1 flex-wrap ${hasThumbnail ? 'px-2' : 'justify-center'}`}>
          {tags.slice(0, 2).map(t => (
            <span key={t} className="fm-tag-pill px-1.5 py-0.5 text-[10px] truncate max-w-[56px]">{t}</span>
          ))}
          {tags.length > 2 && (
            <span className="text-[10px] text-[var(--rq-text-soft)]">+{tags.length - 2}</span>
          )}
        </div>
      )}
    </div>
  )
}
